import { useMemo, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { Printer, Mail, FileText, Layers, BarChart3, Settings as SettingsIcon, Files } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { usePayroll } from '@/context/AppContext';
import { useToast } from '@/hooks/useToast';
import { calcStats } from '@/lib/payroll';
import {
  EnvelopePage,
  A4Page,
  PayrollSummaryReport,
  ResultsStatsReport,
} from '@/components/print/PrintPages';

type PrintMode = 'envelope' | 'a4' | 'payroll-summary' | 'stats-only' | 'all-consolidated';

export function PrintPage() {
  const {
    payrollData,
    period,
    rawById,
    adminAdj,
    printEmployees,
    setPrintEmployees,
    companyName,
  } = usePayroll();
  const { show } = useToast();
  const [busy, setBusy] = useState(false);

  const stats = calcStats(payrollData);

  // ✅ قائمة الموظفين مرتبة أبجدياً (الاسم) لاستخدامها في dropdown والطباعة
  const sortedEmployees = useMemo(() => {
    if (!payrollData) return [];
    return [...payrollData].sort((a: any, b: any) =>
      String(a.Name || '').localeCompare(String(b.Name || ''), 'ar'),
    );
  }, [payrollData]);

  const handlePrint = (mode: PrintMode) => {
    if (!payrollData || !stats) {
      show('لا توجد بيانات للطباعة', 'error');
      return;
    }
    setBusy(true);
    // ✅ ترتيب أبجدي للموظفين (سواء "الكل" أو موظف واحد)
    const target =
      printEmployees === 'all'
        ? [...payrollData].sort((a: any, b: any) =>
            String(a.Name || '').localeCompare(String(b.Name || ''), 'ar'),
          )
        : payrollData.filter((d: any) => d.ID === printEmployees);
    if (target.length === 0) {
      show('لا يوجد موظف للطباعة', 'error');
      setBusy(false);
      return;
    }

    const printRoot = document.getElementById('print-root');
    if (!printRoot) {
      show('لم يتم العثور على منطقة الطباعة', 'error');
      setBusy(false);
      return;
    }
    printRoot.innerHTML = '';

    const appendHTML = (html: string) => {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;
      while (wrapper.firstChild) printRoot.appendChild(wrapper.firstChild);
    };

    if (mode === 'payroll-summary') {
      appendHTML(renderToString(
        <PayrollSummaryReport
          employees={target}
          period={period}
          adminAdj={adminAdj}
          companyName={companyName}
        />,
      ));
    } else if (mode === 'stats-only') {
      appendHTML(renderToString(
        <ResultsStatsReport
          employees={target}
          period={period}
          stats={stats}
          companyName={companyName}
        />,
      ));
    } else if (mode === 'all-consolidated') {
      // ✅ طباعة كل التقارير المجمعة: تقرير كشف المرتبات + تقرير الإحصائيات — كل واحد في A4 Landscape
      appendHTML(renderToString(
        <PayrollSummaryReport
          employees={target}
          period={period}
          adminAdj={adminAdj}
          companyName={companyName}
        />,
      ));
      appendHTML(renderToString(
        <ResultsStatsReport
          employees={target}
          period={period}
          stats={stats}
          companyName={companyName}
        />,
      ));
    } else {
      // ✅ التقارير التفصيلية تطلع لكل موظف - كل موظف في صفحة منفصلة
      // لازم نحتفظ بالـ wrapper (مش نفككه) عشان position:absolute بيلغي page-break
      target.forEach((emp: any) => {
        const wrap = document.createElement('div');
        // Wrapper class حسب نوع الطباعة - كل واحد بحجم صفحته
        if (mode === 'envelope') {
          wrap.className = 'envelope-page-wrapper';
        } else if (mode === 'a4') {
          wrap.className = 'a4-page-wrapper';
        }
        if (mode === 'envelope') {
          // ✅ النسق التفصيلي لكل موظف في ظرف منفصل
          wrap.innerHTML = renderToString(
            <EnvelopePage
              employee={emp}
              period={period}
              rawDays={rawById[emp.ID] || []}
              companyName={companyName}
            />,
          );
        } else if (mode === 'a4') {
          wrap.innerHTML = renderToString(
            <A4Page
              employee={emp}
              period={period}
              rawDays={rawById[emp.ID] || []}
              companyName={companyName}
            />,
          );
        }
        printRoot.appendChild(wrap); // نحتفظ بالـ wrapper - هو اللي بيخلي كل موظف في صفحة
      });
    }

    // Set page size hint (browser will still use OS print dialog, but we also use CSS @page)
    setTimeout(() => {
      window.print();
      setBusy(false);
    }, 200);
  };

  if (!payrollData) {
    return (
      <div className="space-y-5 animate-slide-up">
        <h2 className="text-2xl font-extrabold flex items-center gap-2">
          <Printer size={22} /> الطباعة الاحترافية
        </h2>
        <Card className="text-center !p-12">
          <p className="text-slate-600 dark:text-slate-300">احسب المرتبات أولاً</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-slide-up">
      <h2 className="text-2xl font-extrabold flex items-center gap-2">
        <Printer size={22} /> الطباعة الاحترافية
      </h2>

      <Card accent="slate" padding="sm">
        <p className="text-xs text-slate-600 dark:text-slate-400">
          💡 <strong>ملاحظة:</strong> في نافذة الطباعة، اختار حجم الورق المناسب (A4 / Envelope DL) وحدد
          الهوامش الافتراضية. النظام بيخلّي كل موظف في صفحة منفصلة تلقائياً.
        </p>
      </Card>

      {/* Options */}
      <Card padding="md">
        <label className="text-sm font-semibold mb-2 block">الموظف</label>
        <select
          value={printEmployees}
          onChange={(e) => setPrintEmployees(e.target.value)}
          className="form-input"
        >
          <option value="all">جميع الموظفين ({payrollData.length})</option>
          {sortedEmployees.map((d: any) => (
            <option key={d.ID} value={d.ID}>
              {d.Name} — {d.Job}
            </option>
          ))}
        </select>
        <p className="text-[11px] text-slate-500 mt-2">
          💡 الموظفين مرتبين أبجدياً • طباعة الظرف دايماً بالنسق <strong>التفصيلي</strong> (كل موظف في ظرف لوحده)
        </p>
      </Card>

      {/* Print options */}
      <div className="grid sm:grid-cols-2 gap-4">
        <PrintOption
          icon={Mail}
          title="ظرف (DL 110×220mm)"
          desc="مغلف لكل موظف — مناسب لتوزيع المرتبات في ظرف"
          color="from-indigo-500 to-violet-500"
          onClick={() => handlePrint('envelope')}
          disabled={busy}
          badge="ظروف"
        />
        <PrintOption
          icon={FileText}
          title="A4 — كشف موظف تفصيلي"
          desc="صفحة كاملة لكل موظف بكل تفاصيل الحضور والجزاءات"
          color="from-sky-500 to-blue-500"
          onClick={() => handlePrint('a4')}
          disabled={busy}
          badge="A4"
        />
        <PrintOption
          icon={Layers}
          title="تقرير مجمع A4 Landscape"
          desc="كل الموظفين في تقرير واحد A4 أفقي مع التوقيعات"
          color="from-emerald-500 to-teal-500"
          onClick={() => handlePrint('payroll-summary')}
          disabled={busy}
          badge="Landscape"
        />
        <PrintOption
          icon={BarChart3}
          title="إحصائيات تفصيلية"
          desc="تقرير إحصائي A4 أفقي — تقسيم حسب الوظيفة والمؤشرات"
          color="from-fuchsia-500 to-pink-500"
          onClick={() => handlePrint('stats-only')}
          disabled={busy}
          badge="Landscape"
        />
      </div>

      {/* ✅ اطبع كل التقارير المجمعة دفعة واحدة */}
      <button
        type="button"
        onClick={() => handlePrint('all-consolidated')}
        disabled={busy}
        className="w-full text-right p-5 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-4"
      >
        <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm shrink-0">
          <Files size={26} />
        </div>
        <div className="flex-1">
          <div className="font-extrabold text-lg flex items-center gap-2">
            اطبع كل التقارير المجمعة
            <Badge className="bg-white/20 text-white">الأفضل</Badge>
          </div>
          <div className="text-xs opacity-90 mt-1 leading-relaxed">
            تقرير كشف المرتبات + تقرير الإحصائيات في مهمة طباعة واحدة (A4 Landscape لكل تقرير)
          </div>
        </div>
        <Printer size={20} className="shrink-0" />
      </button>

      {/* Print hints */}
      <Card accent="indigo" padding="md">
        <h3 className="font-bold mb-3 flex items-center gap-2">
          <SettingsIcon size={18} className="text-indigo-500" /> إعدادات الطباعة الموصى بها
        </h3>
        <div className="grid sm:grid-cols-3 gap-3 text-xs">
          <div className="p-3 rounded-xl bg-indigo-50/60 dark:bg-indigo-900/10">
            <div className="font-bold text-indigo-700 dark:text-indigo-300 mb-1">ظرف DL</div>
            <ul className="text-slate-600 dark:text-slate-400 space-y-0.5 leading-relaxed">
              <li>• الحجم: 110×220 mm</li>
              <li>• الاتجاه: Portrait</li>
              <li>• الهوامش: Default</li>
              <li>• بدون ألوان (Grayscale)</li>
            </ul>
          </div>
          <div className="p-3 rounded-xl bg-sky-50/60 dark:bg-sky-900/10">
            <div className="font-bold text-sky-700 dark:text-sky-300 mb-1">A4 موظف</div>
            <ul className="text-slate-600 dark:text-slate-400 space-y-0.5 leading-relaxed">
              <li>• الحجم: A4 (210×297 mm)</li>
              <li>• الاتجاه: Portrait</li>
              <li>• الهوامش: Default</li>
              <li>• صفحة لكل موظف</li>
            </ul>
          </div>
          <div className="p-3 rounded-xl bg-emerald-50/60 dark:bg-emerald-900/10">
            <div className="font-bold text-emerald-700 dark:text-emerald-300 mb-1">A4 Landscape</div>
            <ul className="text-slate-600 dark:text-slate-400 space-y-0.5 leading-relaxed">
              <li>• الحجم: A4 (297×210 mm)</li>
              <li>• الاتجاه: Landscape</li>
              <li>• الهوامش: Default</li>
              <li>• تقرير مجمع</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}

function PrintOption({
  icon: Icon,
  title,
  desc,
  color,
  onClick,
  disabled,
  badge,
}: {
  icon: any;
  title: string;
  desc: string;
  color: string;
  onClick: () => void;
  disabled?: boolean;
  badge: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`text-right p-5 rounded-2xl bg-gradient-to-br ${color} text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
          <Icon size={22} />
        </div>
        <div className="flex-1">
          <div className="font-extrabold text-base">{title}</div>
        </div>
        <Badge className="bg-white/20 text-white">{badge}</Badge>
      </div>
      <div className="text-xs opacity-90 leading-relaxed">{desc}</div>
    </button>
  );
}
